import { Directive, input, output, signal, computed, model, inject, DestroyRef, OnInit} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, Observable, skip, tap } from 'rxjs';


/**
 * Utilisation 
 * 
 * 
 * 
 *  <input 
      [(ngModel)]="qSearch"         => uniquement sur les input permettant de mettre à jour la valeur qSearh lors de la frappe de l'utilisateur
      #searchable="searchable"      => lien vers la directive permettant de lancer les méthodes next et prev et la récupération de lapage courrante
      [qSearch]="qSearch()"         => terme de recherche
      [search]="getSearchObs()"     => fonction qui retourne, un observable qui lance la recherche fn(q, start, length)
      [(results)]="users"           => liste des resultats retournés par la recherche
      [(searching)]="searching"     => evt qui est émit lors du debut/fin recherche
      [length]                      => taille de la recherche
      [autoload]                    => optionnel : indique si on lance la premiere recherche automatiquement (true par defaut)
      [start]                       => optionnel : indique à quelle indic on doit commencer (attention indice pas page)
      (setPage)                     => evt emit lors du changement de page
      type="text" 
      class="grow placeholder:text-base-content/60"
      placeholder="Recherchez un utilisateur..." 
    />
 */


@Directive({
  selector: '[qSearch]',
  standalone: false,
  exportAs: 'searchable' 
})
export class Searchable implements OnInit{
  readonly searchObs=input.required<(q: string, start: number, length: number) => Observable<any>>({alias: 'search'});   //méthode qui retourne l'observable pour la recherche
  readonly qSearch=input.required<string>();       //chaine de caractère à rechercher
  readonly qSearch$=toObservable(this.qSearch);    //observable de la chaine de recherche
  readonly length=input<number>(100);
  readonly results=model<any>();                   //evt pour la remonté des resultats
  readonly error=output<any>();                    //evt en cas d'erreur dans la recherche
  readonly searching=model<boolean>(true);         //evt déclanché en débu/fin de recherche
  readonly autoload=input(true);                   //indique si on fait le premier chargement
  readonly start=input(0);                         //indique à quelle page on doit commencer
  private isReady: boolean=true;                   //permet de dire si on a passé la premiere tentative de chargement
  private isFirst: boolean=true;                   //permet de dire si c'est la premiere fois qu'on fait un chargement (utile pour start/page)

  readonly page=signal<number>(0);
  readonly setPage=output<number>();

  readonly canPrev=computed(() => this.page()>0);
  readonly canNext=computed(() => this.results().length==this.length());
  private readonly destroyRef=inject(DestroyRef);
  private destroyed=false;


  constructor(){
    toObservable(this.page)
      .pipe(
        skip(1)
      )
      .subscribe(page => this.setPage.emit(page));
    this.destroyRef.onDestroy(() => {
      this.destroyed=true;
    });
  }

  ngOnInit(){
    this.qSearch$
      .pipe(
        filter(query => (!query || query.length >= 3)),     //recherche que si on a un qsearch >= "char"
        filter(() => {
          //recherche lors du chargement uniquement is on a autoload=true
          const canSearch=(!this.isReady || this.autoload());
          this.isReady=false;
          return canSearch;
        }),
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.search());
  }

  search(){
    this.page.set(((this.isFirst)? this.start() : 0));
    this.isFirst=false;
    this.lunchSearch();
  }

  lunchSearch(){
    this.searching.set(true);

    //trim de la recherche pour mettre null si vide
    let q: string=this.qSearch();
    if(q!=null && q.trim()=='')
      q=null;
    else if(q!=null)
      q=q.trim().toLowerCase();

    this.searchObs()(q, this.page()*this.length(), this.length())
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (results: any) => {
          if(!this.destroyed)
            this.results.set(results);
        },
        error: (error: any) => {
          if(!this.destroyed)
            this.error.emit(error);
        },
        complete: () => {
          if(!this.destroyed)
            this.searching.set(false);
        }
      });
  }

  prev(){
    if(this.canPrev()){
      this.page.set(this.page()-1);
      this.lunchSearch();
    }
  }

  next(){
    if(this.canNext()){
      this.page.set(this.page()+1);
      this.lunchSearch();
    }
  }
}
